import { z } from "zod"

export const METRIC_DIMS = ["user", "module", "country", "entity", "class", "bucket"] as const
export type MetricDim = (typeof METRIC_DIMS)[number]

export const GRANULARITIES = ["day", "week", "month", "all"] as const
export type Granularity = (typeof GRANULARITIES)[number]

export const metricIdSchema = z
  .string()
  .regex(/^[a-z][a-z0-9_]{2,48}$/, "metric id must be snake_case, 3-49 characters")

export const metricDimsSchema = z
  .object({
    user: z.string().optional(),
    module: z.string().optional(),
    country: z.string().regex(/^[A-Za-z]{2,3}(-[A-Za-z0-9]{1,3})?$/).optional(),
    entity: z.string().optional(),
    class: z.string().optional(),
    bucket: z.string().optional(),
  })
  .strict()

export const metricPointSchema = z
  .object({
    metric: metricIdSchema,
    periodStart: z.iso.datetime({ offset: true }),
    granularity: z.enum(GRANULARITIES),
    dims: metricDimsSchema.default({}),

    valueNum: z.number().finite().nullable().optional(),
    valueText: z.string().nullable().optional(),
    valueTs: z.iso.datetime({ offset: true }).nullable().optional(),

    /** Whether this point is approximate — session-duration metrics cannot be measured exactly */
    approximate: z.boolean().optional(),
  })
  .refine(
    (p) => p.valueNum != null || p.valueText != null || p.valueTs != null,
    { message: "Exactly one of the three value slots must be set", path: ["valueNum"] },
  )

export type MetricPoint = z.infer<typeof metricPointSchema>
export type MetricDims = z.infer<typeof metricDimsSchema>

export const VALUE_SLOT = {
  count: "valueNum",
  duration_sec: "valueNum",
  ratio: "valueNum",
  timestamp: "valueTs",
  text: "valueText",
} as const satisfies Record<string, keyof Pick<MetricPoint, "valueNum" | "valueText" | "valueTs">>
